import { useObjectUrlCacheManager } from 'hooks/objectUrl'
import { forwardRef, type ForwardedRef, useEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import { useRepository } from 'hooks/auth'
import { observer } from 'mobx-react-lite'
import { StorageFetchError } from '@breakoutlearning/firebase-repository/types'

type GoogleStorageVideoProps = {
  src?: string
  vttSrc?: string
  vttLabel?: string
  alt: string
  loader?: React.ReactNode
  namespace: string
  skipCache?: boolean
  decoding?: 'sync' | 'async' | 'auto'
  disablePictureInPicture?: boolean
  disableRemotePlayback?: boolean
  children?: React.ReactNode
} & React.MediaHTMLAttributes<HTMLVideoElement>

function useAuthenticatedBlobUrl(
  src: string | undefined,
  namespace: string,
  skipCache?: boolean
) {
  const { authToken } = useRepository()
  const objectUrlCache = useObjectUrlCacheManager()
  const initialObjectUrl = skipCache
    ? undefined
    : objectUrlCache.get(namespace, src)
  const [objectUrl, setObjectUrl] = useState<string | undefined>(
    initialObjectUrl
  )
  const [retries, setRetries] = useState(0)

  useEffect(() => {
    if (!src || !authToken) return
    if (objectUrl) return
    if (retries >= 3) return

    const options = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }

    setTimeout(
      () => {
        fetch(src, options)
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            return res.blob()
          })
          .then((blob) => {
            const newObjectURL = URL.createObjectURL(blob)
            if (!skipCache) {
              objectUrlCache.set(namespace, src, newObjectURL)
            }
            setObjectUrl(newObjectURL)
          })
          .catch((err) => {
            if (retries < 3) {
              setRetries(retries + 1)
            } else {
              captureException(
                new StorageFetchError(`Failed to fetch storage asset: ${src}`, {
                  cause: err,
                })
              )
              console.error('Failed to fetch asset', err)
            }
          })
      },
      1000 * retries * 2 // exponential backoff - 0s, 2s, 4s, 8s
    )
  }, [authToken, src, objectUrlCache, namespace, skipCache, objectUrl, retries])

  return objectUrl
}

const GoogleStorageNoObserver = forwardRef(function GoogleStorageVideo(
  {
    src,
    vttSrc,
    vttLabel,
    namespace,
    skipCache,
    loader,
    children,
    ...rest
  }: GoogleStorageVideoProps,
  ref: ForwardedRef<HTMLVideoElement>
) {
  const objectUrl = useAuthenticatedBlobUrl(src, namespace, skipCache)
  const vttObjectUrl = useAuthenticatedBlobUrl(
    vttSrc,
    `${namespace}-vtt`,
    skipCache
  )

  if (!objectUrl) return loader || null

  return (
    <video ref={ref} src={objectUrl} {...rest}>
      {vttObjectUrl && (
        <track
          kind="subtitles"
          srcLang="en"
          label={vttLabel ?? 'English'}
          src={vttObjectUrl}
        />
      )}
      {children}
    </video>
  )
})

export const GoogleStorageVideo = observer(function GoogleStorageVideo(
  props: GoogleStorageVideoProps & { ref?: ForwardedRef<HTMLVideoElement> }
) {
  return <GoogleStorageNoObserver {...props} ref={props.ref} />
})
